import { SocialSharing } from '@change/ds-core-ui';

import { useResolvedLabel } from '#src/app/shared/hooks/i18n';
import type { ShareTrackingDataOverride } from '#src/app/shared/hooks/share';
import { useDeviceMessagingIcon } from '#src/app/shared/hooks/share';

import { SOCIAL_SHARING_ICON_CONFIG_BY_CHANNEL } from './constants';
import { useRoundShareIconButton } from './hooks/useRoundShareIconButton';
import type { RoundShareIconButtonConfig } from './types';

export type { RoundShareIconButtonChannel, RoundShareIconButtonConfig } from './types';

type Props = {
	buttonConfig: RoundShareIconButtonConfig;
	className?: string;
	/** The DS `SocialSharing` size. Defaults to the DS default. */
	size?: React.ComponentProps<typeof SocialSharing>['size'];
	onBeforeShare?: () => void;
	trackingData?: ShareTrackingDataOverride;
};

export const RoundShareIconButton = ({
	buttonConfig: { ShareAction, label, channel, successLabel },
	className,
	size,
	onBeforeShare,
	trackingData,
	...rest // for data attributes
}: Props): React.JSX.Element => {
	const { MessagingIconComponent, variant: smsVariant } = useDeviceMessagingIcon();
	const { data, actions } = useRoundShareIconButton({ label, successLabel });
	const ariaLabel = useResolvedLabel(label);
	const buttonLabel = useResolvedLabel(data.buttonLabel);

	const { icon, variant } =
		channel === 'sms'
			? { icon: <MessagingIconComponent />, variant: smsVariant }
			: SOCIAL_SHARING_ICON_CONFIG_BY_CHANNEL[channel];

	return (
		<ShareAction onBeforeAction={onBeforeShare} onAfterAction={actions.afterShare} trackingData={trackingData}>
			<SocialSharing
				icon={icon}
				label={buttonLabel}
				aria-label={ariaLabel}
				variant={variant}
				size={size}
				className={className}
				data-testid={`${channel}-share-button`}
				{...rest}
			/>
		</ShareAction>
	);
};
